import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Default marker icon fix for bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const blueIcon = new L.DivIcon({
  className: "amar-map-marker",
  html: `<div style="width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:700;color:white;background:#1D4ED8;border:2px solid white;border-radius:50%;box-shadow:0 4px 12px rgba(29,78,216,0.4);font-size:13px;"></div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

function makeCountIcon(count) {
  return new L.DivIcon({
    className: "amar-map-marker",
    html: `<div style="width:44px;height:44px;display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:700;color:white;background:#1D4ED8;border:2px solid white;border-radius:50%;box-shadow:0 6px 16px rgba(29,78,216,0.45);font-size:14px;">${count}</div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 44],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
  }, [map, points]);
  return null;
}

export default function AvailabilitiesMap({ projects, availabilities, onProjectClick }) {
  // Group availabilities per project_id
  const byProject = useMemo(() => {
    const map = {};
    for (const a of availabilities) {
      const pid = a.project_id || "_unknown";
      if (!map[pid]) map[pid] = [];
      map[pid].push(a);
    }
    return map;
  }, [availabilities]);

  const points = useMemo(() => {
    return projects
      .filter((p) => p.gps_lat && p.gps_lng)
      .map((p) => ({
        id: p.id,
        slug: p.slug,
        name: p.name,
        city: p.city,
        lat: p.gps_lat,
        lng: p.gps_lng,
        cover: p.cover_image,
        count: (byProject[p.id] || []).filter((a) => a.status === "disponible").length,
        total: (byProject[p.id] || []).length,
      }));
  }, [projects, byProject]);

  const defaultCenter = [35.7, -0.6]; // Oran area

  return (
    <div className="h-[520px] w-full border border-neutral-200" data-testid="availabilities-map">
      <MapContainer
        center={defaultCenter}
        zoom={11}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {points.map((p) => (
          <Marker
            key={p.id}
            position={[p.lat, p.lng]}
            icon={p.count > 0 ? makeCountIcon(p.count) : blueIcon}
          >
            <Popup>
              <div style={{ minWidth: 200, fontFamily: "'Outfit', sans-serif" }}>
                {p.cover && <img src={p.cover} alt={p.name} style={{ width: "100%", height: 100, objectFit: "cover", marginBottom: 8 }} />}
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, marginBottom: 4 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>{p.city}</div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>
                  <strong style={{ color: "#1D4ED8" }}>{p.count}</strong> disponible(s) · {p.total} total
                </div>
                <button
                  onClick={() => onProjectClick?.(p.id)}
                  style={{
                    width: "100%", padding: "8px 12px", background: "#1D4ED8", color: "white",
                    border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer",
                    textTransform: "uppercase", letterSpacing: "0.08em",
                  }}
                >
                  Voir les biens
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
        <FitBounds points={points} />
      </MapContainer>
    </div>
  );
}
